<template>
  <div
    ref="containerRef"
    class="image-thumbnail-strip"
    :class="{
      'image-thumbnail-strip--horizontal': layout === 'carousel',
      'image-thumbnail-strip--vertical': layout === 'grid',
    }"
  >
    <div ref="scrollRef" class="image-thumbnail-strip__scroll">
      <button
        v-for="(item, index) in sizedItems"
        :key="item.uid"
        :ref="(el) => setItemRef(index, el as HTMLElement | null)"
        class="image-thumbnail-strip__item"
        :class="{
          'image-thumbnail-strip__item--active': index === currentIndex,
        }"
        @click="handleSelect(index)"
      >
        <img
          v-if="item.thumbnailUrl"
          :src="item.thumbnailUrl"
          :alt="item.name"
          class="image-thumbnail-strip__image"
          loading="lazy"
        />
        <div v-else class="image-thumbnail-strip__placeholder">
          <v-icon size="24" color="grey" :icon="mdiImage" />
        </div>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { mdiImage } from '@mdi/js'
import type { ImageAssetListItem } from '../../types/asset'
import type { ImageLayout } from '../types'
import { withThumbnailSize } from '../../common/thumbnail'

const props = defineProps<{
  items: ImageAssetListItem[]
  currentIndex: number
  layout: ImageLayout
}>()

const sizedItems = computed(() =>
  props.items.map((item) => ({
    ...item,
    // A 100px square tile (64–84 on a small screen), cropped by its own `object-fit: cover`.
    thumbnailUrl: withThumbnailSize(item.thumbnailUrl, 240, 240, 'cover'),
  })),
)

const emit = defineEmits<{
  select: [index: number]
}>()

// Refs
const containerRef = ref<HTMLElement | null>(null)
const scrollRef = ref<HTMLElement | null>(null)
const itemRefs = new Map<number, HTMLElement>()

function setItemRef(index: number, el: HTMLElement | null): void {
  if (el) {
    itemRefs.set(index, el)
  } else {
    itemRefs.delete(index)
  }
}

function handleSelect(index: number): void {
  emit('select', index)
}

function scrollToCurrentItem(): void {
  const itemEl = itemRefs.get(props.currentIndex)
  if (itemEl && scrollRef.value) {
    itemEl.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  }
}

// Watch for currentIndex changes
watch(
  () => props.currentIndex,
  () => {
    nextTick(() => {
      scrollToCurrentItem()
    })
  },
)
</script>

<style lang="scss" scoped>
.image-thumbnail-strip {
  background: rgba(var(--v-theme-surface-variant), 0.5);
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));

  &--horizontal {
    height: 80px;

    .image-thumbnail-strip__scroll {
      display: flex;
      flex-direction: row;
      overflow-x: auto;
      overflow-y: hidden;
      padding: 8px;
      gap: 8px;
      scrollbar-width: thin;

      &::-webkit-scrollbar {
        height: 6px;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(var(--v-border-color), 0.5);
        border-radius: 3px;
      }
    }

    .image-thumbnail-strip__item {
      flex: 0 0 auto;
      width: 64px;
      height: 64px;
    }
  }

  &--vertical {
    width: 100px;
    height: 100%;

    .image-thumbnail-strip__scroll {
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 8px;
      gap: 8px;
      height: 100%;
      scrollbar-width: thin;

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(var(--v-border-color), 0.5);
        border-radius: 3px;
      }
    }

    .image-thumbnail-strip__item {
      flex: 0 0 auto;
      width: 84px;
      height: 84px;
    }
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid transparent;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    background: rgb(var(--v-theme-surface));
    transition: border-color 0.15s ease, transform 0.15s ease;
    padding: 0;

    &:hover {
      border-color: rgba(var(--v-theme-primary), 0.5);
    }

    &--active {
      border-color: rgb(var(--v-theme-primary));
      transform: scale(1.05);
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: rgba(var(--v-theme-surface-variant), 0.5);
  }
}
</style>
